import React, {useState, useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import Select from "react-select";
import { tempeFabricationClientWrapper } from '../../../services/tempeFabricationClientWrapper';

// reCAPTCHA site key from environment
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_GOOGLE_CAPTCHA_SITE_KEY;

// Declare grecaptcha on window for TypeScript
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
      .min(1, "Name must be 1 characters at minimum")
      .required("Email is required"),
  subject: Yup.mixed()
      .oneOf(["Quote Inquiry", "Customer Support"])
      .required(),
  email: Yup.string()
      .email("Invalid email address format")
      .required("Email is required"),
  msg: Yup.string()
      .min(5, "Message must be 5 characters at minimum")
      .required("Message is required"),
  phone: Yup.string()
      .notRequired()
      .nullable()
      .when('phone', {
          is: (value: string | null | undefined) => (value?.length ?? 0) > 0,
          then: (rule) => rule.matches(
                  RegExp("^[+]?[0-9]{0,3}?[- .]?[(]?[0-9]{3}[)]?[- .]?[0-9]{3}[- .]?[0-9]{4,6}$"),
                  "Invalid phone number format"
          )
      }),
}, [['phone', 'phone']]);

type FormData = Yup.InferType<typeof validationSchema>;

function ContactForm() {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const [isButtonFormSubmitting, setIsButtonFormSubmitting] = useState(false);
  const [formSubmissionError, setFormSubmissionError] = useState("");
  const [formSubmissionSuccess, setFormSubmissionSuccess] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // Load reCAPTCHA script on component mount
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      console.warn('reCAPTCHA site key not configured');
      return;
    }

    // Check if script already loaded
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        setRecaptchaLoaded(true);
      });
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts before load completes
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  function renderFormSubmissionButtonLabel() {
    if (isButtonFormSubmitting) {
      return (<><span> <label> Submitting...</label></span></>)
    } else {
      if (formSubmissionError !== "") {
        return (
            <><span> <label className="color-orange1">{formSubmissionError}</label></span></>
        )
      } else if (formSubmissionSuccess) {
        return (
            <><span> <label>Your message has been sent successfully</label></span></>
        )
      } else return (<><span> <label></label></span></>)
    }
  }

  const onSubmit = async (data: FormData) => {
    try {
      setIsButtonFormSubmitting(true);
      setFormSubmissionSuccess(false);
      setFormSubmissionError("");

      // Get reCAPTCHA token
      let captchaToken: string | undefined;
      if (RECAPTCHA_SITE_KEY && recaptchaLoaded && window.grecaptcha) {
        try {
          captchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact_form' });
        } catch (captchaErr) {
          console.error('Failed to get reCAPTCHA token:', captchaErr);
          setFormSubmissionError('Security verification failed. Please refresh the page and try again.');
          setIsButtonFormSubmitting(false);
          return;
        }
      }

      const response = await tempeFabricationClientWrapper.sendContactFormEmail({
        senderEmail: data.email,
        senderName: data.name,
        subject: data.subject as string,
        message: data.msg,
        senderPhone: data.phone || undefined,
        additionalInformation: captchaToken ? { captchaToken } : undefined,
      });

      if (response.error) {
        console.error('Error submitting form:', response.error);
        setFormSubmissionError(response.error);
      } else {
        console.log('Form submitted successfully');
        setFormSubmissionSuccess(true);
      }
    } catch (err) {
      console.error('Failed to submit the form', err)
      setFormSubmissionError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsButtonFormSubmitting(false);
    }
  };

  const subjectOptions = [
    { label: "Quote Inquiry", value: "Quote Inquiry" },
    { label: "Customer Support", value: "Customer Support" },
  ];

  return (
    <section className="tc-contact-form-style1">
      <div className="container">
        <div className="row justify-content-between">
          <div className="col-lg-4">
            <div className="info wow fadeInUp slow" data-wow-delay="0.2s">
              <h3 className="fsz-45 fw-500 mb-80">
                Let us build your dream!
              </h3>
              <p className="fsz-14 color-666 mt-15">
                Your information will only be used to get back to you. <br /> Required fields
                are marked <span className="text-danger"> * </span>
              </p>

              {/* Order Help Section */}
              <div className="mt-60">
                <h5 className="fsz-20 fw-500 mb-20">Need Help with an Order?</h5>
                <div className="d-flex flex-column gap-3">
                  <a
                    href="/find-order"
                    className="d-flex align-items-center text-decoration-none"
                    style={{ color: '#666' }}
                  >
                    <i className="fal fa-search me-2" style={{ fontSize: '18px', color: 'var(--color-orange1)' }}></i>
                    <span>Find My Order</span>
                  </a>
                  <a
                    href="/order-status"
                    className="d-flex align-items-center text-decoration-none"
                    style={{ color: '#666' }}
                  >
                    <i className="fal fa-info-circle me-2" style={{ fontSize: '18px', color: 'var(--color-orange1)' }}></i>
                    <span>Check Order Status</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="form mt-5 mt-lg-0 wow fadeInUp slow"
              data-wow-delay="0.4s"
            >
              <div className="row">
                <div className="col-lg-6">
                  <div className="form-group mb-30">
                    <label htmlFor="">
                      Full name <span className="color-orange1"> * </span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="your name"
                      id="name" {...register('name')}
                    />
                    {errors.name && <span className="color-orange1">{errors.name.message}</span>}
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="form-group mb-30">
                    <label htmlFor="">
                      email address <span className="color-orange1">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Your email address"
                      id="email" {...register('email')}
                    />
                    {errors.email && <span className="color-orange1">{errors.email.message}</span>}
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="form-group mb-30">
                    <label htmlFor="">
                      Phone <span className="color-999"> (optional) </span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Your phone number"
                      id="phone" {...register('phone')}
                    />
                    {errors.phone && <span className="color-orange1">{errors.phone.message}</span>}
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="form-group mb-30">
                    <label htmlFor="">
                      subject <span className="color-orange1"> * </span>
                    </label>
                    <Controller
                        name="subject"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                options={subjectOptions}
                                className="form-select form-control"
                                value={subjectOptions.find((c) => c.value === value)}
                                onChange={(val) => onChange(val?.value)}
                            />
                        )}
                    />
                    {errors.subject && <span className="color-orange1">{errors.subject.message}</span>}
                  </div>
                </div>
                <div className="col-lg-12">
                  <div className="form-group mb-30">
                    <label htmlFor=""> message  <span className="color-orange1"> * </span></label>
                    <textarea
                      rows={6}
                      placeholder="Write your message here"
                      className="form-control"
                      id="msg" {...register('msg')}
                    ></textarea>
                    {errors.msg && <span className="color-orange1">{errors.msg.message}</span>}
                  </div>
                </div>
              </div>
              <button
                type="submit"
                className="butn hover-bg-orange1 text-capitalize bg-white rounded-pill mt-40"
                disabled={isButtonFormSubmitting}
              >
                <span>
                  Send Your Message
                  <i className="fal fa-arrow-up-right ms-2"></i>
                </span>
              </button>
              <div className="fsz-12 color-666 mt-20">
                { renderFormSubmissionButtonLabel() }
              </div>
            </form>
          </div>
        </div>
      </div>
      <img
        src="/innerpages/assets/img/contact_shap.png"
        alt=""
        className="shap"
      />
    </section>
  );
}

export default ContactForm;
